import React from 'react';
import { BUCKET_TONE, RESELLER_TONE, barHeight, compact, niceMax, pageWindow } from './format';

/** Page title row: heading on the left, actions on the right. */
export const PageHeader = ({ eyebrow, title, children }) => (
  <div className="ds-pagehead">
    <div>
      {eyebrow && <p className="ds-eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
    </div>
    {children && <div className="ds-actions">{children}</div>}
  </div>
);

export const Panel = ({ title, subtitle, action, children, className = '', wide }) => (
  <section className={`ds-panel ${wide ? 'is-wide' : ''} ${className}`}>
    {(title || action) && (
      <div className="ds-panel-head">
        <div>
          {title && <h2>{title}</h2>}
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

/** One headline number. tone="accent" gives the dark highlight card. */
export const Kpi = ({ label, value, note, tone, noteTone }) => (
  <div className={`ds-kpi ${tone === 'accent' ? 'is-accent' : ''}`}>
    <span className="ds-kpi-label">{label}</span>
    <strong>{value}</strong>
    {note && <span className={`ds-kpi-note ${noteTone || ''}`}>{note}</span>}
  </div>
);

export const KpiGrid = ({ children }) => <section className="ds-kpis" aria-label="Key figures">{children}</section>;

export const Pill = ({ tone, children }) => (
  <span className="ds-pill" style={{ background: tone?.bg, color: tone?.fg }}>{children}</span>
);
export const BucketPill = ({ bucket, label }) => <Pill tone={BUCKET_TONE[bucket] || BUCKET_TONE.Pending}>{label || bucket}</Pill>;
const STATUS_LABEL = { active: 'Active', pending: 'Pending', suspended: 'Inactive' };
export const StatusPill = ({ status }) => (
  <Pill tone={RESELLER_TONE[status] || RESELLER_TONE.suspended}>{STATUS_LABEL[status] || status}</Pill>
);

export const Button = ({ variant = 'secondary', size, className = '', ...props }) => (
  <button type="button" className={`ds-btn ds-btn-${variant} ${size === 'sm' ? 'is-sm' : ''} ${className}`} {...props} />
);

export const Loading = ({ label = 'Loading' }) => <div className="ds-state" role="status">{label}…</div>;

export const ErrorState = ({ message, onRetry }) => (
  <div className="ds-state is-error" role="alert">
    <p>{message}</p>
    {onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}
  </div>
);

export const EmptyState = ({ title, children }) => (
  <div className="ds-empty">
    <strong>{title}</strong>
    {children && <p>{children}</p>}
  </div>
);

/** Wrap fetch states so a page body only renders with data. */
export const Async = ({ state, children }) => {
  if (state.error && !state.data) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;
  return children(state.data);
};

/** Numbered pager for lists that are not DataTables (like the Activity feed). page is 1-based. */
export const Pagination = ({ total, pageSize, page, onChange }) => {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (p) => onChange(Math.min(Math.max(1, p), pages));
  return (
    <div className="ds-pagination">
      <span className="ds-pagination-info">Showing {from} to {to} of {total}</span>
      <nav aria-label="Pagination">
        <button type="button" aria-label="Previous page" disabled={page === 1} onClick={() => go(page - 1)}>‹</button>
        {pageWindow(page, pages).map((p, i) => (p === 'gap'
          ? <span key={`gap-${i}`} className="ds-page-gap" aria-hidden="true">…</span>
          : <button key={p} type="button" aria-label={`Page ${p}`} aria-current={p === page ? 'page' : undefined} className={p === page ? 'is-current' : ''} onClick={() => go(p)}>{p}</button>))}
        <button type="button" aria-label="Next page" disabled={page === pages} onClick={() => go(page + 1)}>›</button>
      </nav>
    </div>
  );
};

export const FilterPills = ({ options, value, onChange, label }) => (
  <div className="ds-pills" role="group" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        aria-pressed={value === o.value}
        className={`ds-filter ${value === o.value ? 'is-on' : ''}`}
        onClick={() => onChange(o.value)}
      >
        {o.label}
        {o.count !== undefined && <span>{o.count}</span>}
      </button>
    ))}
  </div>
);

/**
 * Monthly bars. series = [{label, a, b?}]; `b` draws a second bar (e.g. paid next to earned).
 * Heights come from one scale so every bar and tick agrees.
 */
export const BarChart = ({ series, aLabel, bLabel, aColor = '#0a2540', bColor = '#2d8f6f', ariaLabel, valueLabels }) => {
  const H = 150;
  const max = niceMax(Math.max(0, ...series.flatMap((s) => [s.a, s.b || 0])));
  const ticks = [0, 1, 2, 3].map((i) => (max / 3) * i);
  return (
    <div>
      {bLabel && (
        <div className="ds-legend">
          <span><i style={{ background: aColor }} />{aLabel}</span>
          <span><i style={{ background: bColor }} />{bLabel}</span>
        </div>
      )}
      <div className="ds-chart" role="img" aria-label={ariaLabel}>
        <div className="ds-chart-axis" aria-hidden="true" style={{ height: H }}>
          {ticks.map((t) => (
            <span key={t} style={{ bottom: barHeight(t, max, H) - 7 }}>{compact(t)}</span>
          ))}
        </div>
        <div className="ds-chart-main">
          <div className="ds-chart-plot" style={{ height: H + 18 }}>
            {ticks.slice(1).map((t) => (
              <span key={t} className="ds-gridline" aria-hidden="true" style={{ bottom: barHeight(t, max, H) }} />
            ))}
            {series.map((s, i) => (
              <div className="ds-bargroup" key={s.label + i}>
                {valueLabels && <span className="ds-barvalue">{compact(s.a)}</span>}
                <div className="ds-bars">
                  <span style={{ height: Math.max(barHeight(s.a, max, H), s.a > 0 ? 2 : 0), background: s.highlight || aColor }} />
                  {bLabel && <span style={{ height: Math.max(barHeight(s.b || 0, max, H), s.b > 0 ? 2 : 0), background: bColor }} />}
                </div>
              </div>
            ))}
          </div>
          <div className="ds-chart-x">
            {series.map((s, i) => <span key={s.label + i}>{s.label}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Single stacked bar + legend for status counts. */
export const StatusBar = ({ counts, order }) => {
  const total = order.reduce((n, k) => n + (counts[k] || 0), 0);
  return (
    <>
      <div
        className="ds-stack"
        role="img"
        aria-label={`Status split of ${total}: ${order.map((k) => `${counts[k] || 0} ${k.toLowerCase()}`).join(', ')}`}
      >
        {total === 0 && <span style={{ flex: 1, background: '#e3e9f2' }} />}
        {order.map((k) => (counts[k] ? <span key={k} style={{ flex: counts[k], background: BUCKET_TONE[k].bar }} /> : null))}
      </div>
      <ul className="ds-legendlist">
        {order.map((k) => (
          <li key={k}>
            <i style={{ background: BUCKET_TONE[k].bar }} />
            <span>{k}</span>
            <strong>{counts[k] || 0}</strong>
          </li>
        ))}
      </ul>
    </>
  );
};

/** Horizontal share bars, e.g. top resellers. */
export const HBars = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="ds-hbars">
      {rows.map((r) => (
        <div key={r.key}>
          <span className="ds-hbar-label">{r.label}</span>
          <span className="ds-hbar-track"><span style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <strong>{r.text}</strong>
        </div>
      ))}
    </div>
  );
};

const TONE_DOT = {
  good: ['#1f7a5c', '#dcf5e8'],
  info: ['#2f6fd1', '#e3edfc'],
  warn: ['#c8372a', '#fde8e5'],
  neutral: ['#0a2540', '#e1e9f4'],
};

export const Timeline = ({ events, empty }) => {
  if (!events.length) return <EmptyState title="Nothing yet">{empty}</EmptyState>;
  return (
    <ol className="ds-timeline">
      {events.map((e, i) => {
        const [dot, halo] = TONE_DOT[e.tone] || TONE_DOT.neutral;
        return (
          <li key={`${e.at}-${i}`}>
            <span className="ds-dot" style={{ background: dot, boxShadow: `0 0 0 4px ${halo}` }} />
            <div>
              <strong>{e.title}</strong>
              <span>{[e.meta, e.when].filter(Boolean).join(' · ')}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
